// src/pages/AgentDetails.jsx
import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_AGENT, DELETE_AGENT } from '../graphql/agents';
import { GET_AGENT_PORTFOLIO } from '../graphql/portfolio/queries';
import { ASSIGN_PROPERTY_TO_AGENT, UNASSIGN_PROPERTY_FROM_AGENT } from '../graphql/portfolio/mutations';
import { GET_PROPERTIES } from '../graphql/properties';
import { usePermissions } from '../shared/hooks/usePermissions';

const AgentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [selectedPropertyId, setSelectedPropertyId] = useState('');

  const { loading, error, data } = useQuery(GET_AGENT, {
    variables: { id },
    fetchPolicy: 'network-only'
  });

  const { data: portfolioData, refetch: refetchPortfolio } = useQuery(GET_AGENT_PORTFOLIO, {
    variables: { agentId: id },
    fetchPolicy: 'network-only'
  });

  const { data: propertiesData } = useQuery(GET_PROPERTIES, {
    variables: { pageIndex: 0, pageSize: 200, orderAsc: true, orderField: 'address.street' },
    skip: !permissions.canAssignPortfolio
  });

  const [assignProperty] = useMutation(ASSIGN_PROPERTY_TO_AGENT, {
    onCompleted: () => { setSelectedPropertyId(''); refetchPortfolio(); },
    onError: err => alert('Error assigning property: ' + err.message)
  });

  const [unassignProperty] = useMutation(UNASSIGN_PROPERTY_FROM_AGENT, {
    onCompleted: () => refetchPortfolio(),
    onError: err => alert('Error unassigning property: ' + err.message)
  });

  const [deleteAgent] = useMutation(DELETE_AGENT, {
    onCompleted: () => navigate('/agents'),
    onError: error => {
      console.error('Error deleting agent:', error);
      alert('Failed to delete agent. Please try again.');
    }
  });

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${data?.agent?.firstName} ${data?.agent?.lastName}? This action cannot be undone.`)) {
      deleteAgent({ variables: { id } });
    }
  };
  
  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error.message}</span>
    </div>
  );

  const { agent } = data;
  const formatDate = dateString => {
    if (!dateString) return 'Not specified';
    
    try {
      // Try parsing as ISO date string first
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date format';
      }
      
      // Use toLocaleDateString with the 'en-GB' locale for DD/MM/YYYY format
      return date.toLocaleDateString('en-GB', { 
        year: 'numeric', 
        month: 'numeric', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid date';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Agent Details
        </h1>
        <Link to="/agents" className="flex items-center text-blue-600 hover:text-blue-800">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Agents
        </Link>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex space-x-3">
        <Link 
          to={`/agents/edit/${agent.id}`} 
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </Link>
        <Link 
          to={`/agents/${agent.id}/documents`}
          className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Documents
        </Link>
        <button
          onClick={handleDelete}
          className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Delete
        </button>
      </div>

      {/* Agent Info */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-yellow-100 text-yellow-700 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold mr-4">
              {agent.firstName.charAt(0)}{agent.lastName.charAt(0)}
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{agent.firstName} {agent.lastName}</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                {agent.properties?.length || 0} {agent.properties?.length === 1 ? 'Property' : 'Properties'}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <a href={`mailto:${agent.email}`} className="text-blue-600 hover:underline">{agent.email}</a>
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Primary Phone</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <a href={`tel:${agent.primaryPhone}`} className="text-blue-600 hover:underline">{agent.primaryPhone}</a>
              </dd>
            </div>
            {agent.secondaryPhone && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Secondary Phone</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <a href={`tel:${agent.secondaryPhone}`} className="text-blue-600 hover:underline">{agent.secondaryPhone}</a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Agent Professional Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Professional Information</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Agent professional details and credentials
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            {agent.pprRegistrationNumber && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">PPRA Registration Number</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{agent.pprRegistrationNumber}</dd>
              </div>
            )}
            {agent.status && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    agent.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
                  </span>
                </dd>
              </div>
            )}
            {agent.dateStarted && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Date Started</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{formatDate(agent.dateStarted)}</dd>
              </div>
            )}
            {agent.taxNumber && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Tax Number</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{agent.taxNumber}</dd>
              </div>
            )}
            {agent.nextOfKinName && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Next of Kin</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {agent.nextOfKinName}
                  {agent.nextOfKinPhone && (
                    <span className="ml-2 text-blue-600">
                      <a href={`tel:${agent.nextOfKinPhone}`} className="hover:underline">
                        {agent.nextOfKinPhone}
                      </a>
                    </span>
                  )}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Documentation Status */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Documentation Status</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Current status of agent documentation
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <div className={`h-4 w-4 rounded-full mr-3 ${agent.hasFicaDocument ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className={`text-sm ${agent.hasFicaDocument ? 'text-green-700' : 'text-red-700'}`}>
                FICA Documentation {agent.hasFicaDocument ? 'Complete' : 'Pending'}
              </span>
            </div>
            <div className="flex items-center">
              <div className={`h-4 w-4 rounded-full mr-3 ${agent.hasIdDocument ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className={`text-sm ${agent.hasIdDocument ? 'text-green-700' : 'text-red-700'}`}>
                ID Document {agent.hasIdDocument ? 'Complete' : 'Pending'}
              </span>
            </div>
            <div className="flex items-center">
              <div className={`h-4 w-4 rounded-full mr-3 ${agent.bankingDetails ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className={`text-sm ${agent.bankingDetails ? 'text-green-700' : 'text-red-700'}`}>
                Banking Details {agent.bankingDetails ? 'Complete' : 'Pending'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Properties</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Properties managed by this agent
          </p>
        </div>
        <div className="border-t border-gray-200">
          {agent.properties && agent.properties.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {agent.properties.map(property => (
                <li key={property.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <Link to={`/properties/${property.id}`} className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-blue-600">{property.address?.street}</p>
                      <p className="text-sm text-gray-500">
                        {property.address?.suburb}, {property.address?.city} {property.address?.postalCode}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        property.status === 'occupied' ? 'bg-green-100 text-green-800' :
                        property.status === 'vacant' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {property.status?.charAt(0).toUpperCase() + property.status?.slice(1)}
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-5 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No properties assigned to this agent.</p>
              <div className="mt-4">
                <Link 
                  to="/properties/add" 
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Property
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Portfolio Management */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Portfolio</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Properties assigned to this agent via portfolio</p>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {portfolioData?.agentPortfolio?.filter(p => p.status === 'ACTIVE').length || 0} active
          </span>
        </div>

        {permissions.canAssignPortfolio && (
          <div className="border-t border-gray-200 px-4 py-4 sm:px-6 bg-gray-50">
            <div className="flex space-x-3">
              <select
                value={selectedPropertyId}
                onChange={e => setSelectedPropertyId(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a property to assign...</option>
                {propertiesData?.properties?.items
                  ?.filter(p => !portfolioData?.agentPortfolio?.some(ap => ap.propertyId === p.id && ap.status === 'ACTIVE'))
                  .map(p => (
                    <option key={p.id} value={p.id}>
                      {p.address?.street}, {p.address?.suburb} {p.referenceNumber ? `(${p.referenceNumber})` : ''}
                    </option>
                  ))}
              </select>
              <button
                onClick={() => selectedPropertyId && assignProperty({ variables: { agentId: id, propertyId: selectedPropertyId } })}
                disabled={!selectedPropertyId}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Assign
              </button>
            </div>
          </div>
        )}

        <div className="border-t border-gray-200">
          {portfolioData?.agentPortfolio?.filter(p => p.status === 'ACTIVE').length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {portfolioData.agentPortfolio
                .filter(p => p.status === 'ACTIVE')
                .map(assignment => {
                  const property = propertiesData?.properties?.items?.find(p => p.id === assignment.propertyId);
                  return (
                    <li key={assignment.id} className="px-4 py-4 sm:px-6 flex items-center justify-between">
                      <div>
                        {property ? (
                          <>
                            <Link to={`/properties/${property.id}`} className="text-sm font-medium text-blue-600 hover:underline">
                              {property.address?.street}, {property.address?.suburb}
                            </Link>
                            <p className="text-xs text-gray-500">{property.address?.city} • {property.status}</p>
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">Property ID: {assignment.propertyId}</span>
                        )}
                        <p className="text-xs text-gray-400">Assigned: {new Date(assignment.assignedAt).toLocaleDateString('en-GB')}</p>
                      </div>
                      {permissions.canAssignPortfolio && (
                        <button
                          onClick={() => window.confirm('Remove this property from the agent portfolio?') && unassignProperty({ variables: { agentId: id, propertyId: assignment.propertyId } })}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  );
                })}
            </ul>
          ) : (
            <div className="px-4 py-5 text-center text-sm text-gray-500">
              No properties in portfolio. {permissions.canAssignPortfolio ? 'Use the dropdown above to assign properties.' : ''}
            </div>
          )}
        </div>
      </div>

      {/* System Info */}
      <div className="mt-4 text-sm text-gray-500">
        <p>Created: {formatDate(agent.createdAt)} • Last Updated: {formatDate(agent.updatedAt)}</p>
      </div>
    </div>
  );
};

export default AgentDetails;